import {
  isGroup,
  describeNode,
  type SegmentNode,
  type SegmentGroup,
} from '@mailflow/core/segments';
import { Button } from '@mailflow/ui';
import { ConditionEditor } from './ConditionEditor.js';

/**
 * Rekursiver Editor fuer den Regelbaum.
 *
 * Jede Ebene zeigt ihren Verknuepfungsoperator als Umschalter und ist
 * eingerueckt — die Klammerung ist damit sichtbar, statt im Kopf mitgeführt
 * werden zu muessen. Unter jeder Bedingung steht der zugehoerige deutsche Satz.
 */

export interface TreeContext {
  lists: Array<{ id: string; name: string }>;
  tags: Array<{ id: string; name: string }>;
  campaigns: Array<{ id: string; name: string }>;
}

interface Props {
  node: SegmentNode;
  context: TreeContext;
  onChange: (next: SegmentNode) => void;
  onRemove?: () => void;
  depth?: number;
}

export function SegmentTree({ node, context, onChange, onRemove, depth = 0 }: Props) {
  if (!isGroup(node)) {
    return (
      <div className="rule">
        <div className="rule__body">
          <ConditionEditor condition={node} context={context} onChange={onChange} />
          <p className="rule__sentence">
            {describeNode(node, {
              listNames: Object.fromEntries(context.lists.map((l) => [l.id, l.name])),
              tagNames: Object.fromEntries(context.tags.map((t) => [t.id, t.name])),
            })}
          </p>
        </div>
        {onRemove && (
          <button className="rule__remove" onClick={onRemove} aria-label="Bedingung entfernen">
            ×
          </button>
        )}
      </div>
    );
  }

  return (
    <GroupEditor
      group={node}
      context={context}
      onChange={onChange}
      onRemove={onRemove}
      depth={depth}
    />
  );
}

function GroupEditor({
  group,
  context,
  onChange,
  onRemove,
  depth,
}: {
  group: SegmentGroup;
  context: TreeContext;
  onChange: (next: SegmentNode) => void;
  onRemove?: (() => void) | undefined;
  depth: number;
}) {
  const children = group.children ?? [];

  function replaceChild(index: number, next: SegmentNode) {
    onChange({ ...group, children: children.map((child, i) => (i === index ? next : child)) });
  }

  function removeChild(index: number) {
    onChange({ ...group, children: children.filter((_, i) => i !== index) });
  }

  function addCondition() {
    onChange({
      ...group,
      children: [...children, { type: 'email_event', event: 'open', negate: false }],
    });
  }

  function addGroup() {
    onChange({
      ...group,
      children: [
        ...children,
        {
          op: group.op === 'AND' ? 'OR' : 'AND',
          children: [{ type: 'email_event', event: 'open' }],
        },
      ],
    });
  }

  return (
    <div className="group" data-depth={depth} data-op={group.op}>
      <div className="group__header">
        <div className="group__toggle" role="group" aria-label="Verknüpfung">
          <button
            type="button"
            data-active={group.op === 'AND'}
            onClick={() => onChange({ ...group, op: 'AND' })}
          >
            UND
          </button>
          <button
            type="button"
            data-active={group.op === 'OR'}
            onClick={() => onChange({ ...group, op: 'OR' })}
          >
            ODER
          </button>
        </div>
        <span className="muted text-sm">
          {group.op === 'AND' ? 'alle Bedingungen müssen zutreffen' : 'eine Bedingung genügt'}
        </span>
        {onRemove && (
          <button className="rule__remove" onClick={onRemove} aria-label="Gruppe entfernen">
            ×
          </button>
        )}
      </div>

      <div className="group__children">
        {children.length === 0 && (
          <p className="muted text-sm">
            Noch keine Bedingung — ohne Einschränkung trifft das Segment alle Kontakte.
          </p>
        )}
        {children.map((child, index) => (
          <SegmentTree
            key={index}
            node={child}
            context={context}
            depth={depth + 1}
            onChange={(next) => replaceChild(index, next)}
            onRemove={() => removeChild(index)}
          />
        ))}
      </div>

      <div className="group__actions">
        <Button variant="secondary" onClick={addCondition}>
          + Bedingung
        </Button>
        {depth < 3 && (
          <Button variant="ghost" onClick={addGroup}>
            + Untergruppe
          </Button>
        )}
      </div>
    </div>
  );
}
